/**
 * AdminSettings — the platform-policy config editor.
 *
 * Every threshold, cap, duration, and cadence that the board approved on
 * Aug 1 2026 lives here. Editing a value on this screen updates the
 * localStorage-backed policy immediately; every downstream consumer
 * (wizard cap validation, docs upload trigger, campaign duration display,
 * extension approval flow) reads the current values on the next render.
 *
 * Panels:
 *   1. Active phase toggle       (beta ↔ pilot)
 *   2. Beta launch settings      (grant cap only during beta)
 *   3. Pilot launch settings     (grant cap TBD + docs threshold)
 *   4. Campaign lifecycle        (duration, extensions, disbursement)
 *   5. Audit footer              (last edit timestamp + editor name)
 *
 * When the backend arrives, swap the `updatePolicy` / `resetPolicy` calls
 * for `PUT /api/platform-policy`. Everything else stays the same.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Settings, ShieldCheck, RotateCcw, Save, Info,
} from "lucide-react";
import { SectionHeader, AdminButton } from "./_ui";
import {
  getPolicy, updatePolicy, resetPolicy, subscribePolicy, DEFAULT_POLICY,
} from "../../lib/platformPolicy";

const fmt$ = (n) => (n == null ? "TBD" : `$${Number(n).toLocaleString()}`);

const PHASE_META = {
  beta: {
    tone: "tangerine",
    label: "Beta launch",
    desc: "The current active phase. Live now.",
  },
  pilot: {
    tone: "cobalt",
    label: "Pilot launch",
    desc: "Post-beta expansion. Activates after board review of beta results.",
  },
};

export const AdminSettings = () => {
  const [policy, setPolicy] = useState(getPolicy);
  const [draft, setDraft] = useState(policy);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const un = subscribePolicy((p) => setPolicy(p));
    return un;
  }, []);

  useEffect(() => setDraft(policy), [policy]);

  const patch = (fn) => {
    setDraft((d) => {
      const next = fn(d);
      setDirty(true);
      return next;
    });
  };

  const onSave = () => {
    updatePolicy(draft, "Terri");
    toast.success("Policy saved", {
      description: "The updated values apply immediately across the platform.",
    });
    setDirty(false);
  };

  const onReset = () => {
    resetPolicy("Terri");
    toast.message("Policy reset", {
      description: "Restored to the Aug 1 2026 board-approved defaults.",
    });
    setDirty(false);
  };

  return (
    <div className="space-y-10">
      <SectionHeader
        eyebrow="Settings · Platform policy"
        title="The numbers the board approved."
        description="Every cap, duration, threshold, and cadence lives in a single config document. Change a value here, and every part of the platform (wizard, admin, docs) picks it up on the next render."
        action={
          <div className="flex items-center gap-2">
            <AdminButton
              variant="secondary"
              size="sm"
              onClick={onReset}
              testid="policy-reset"
            >
              <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.75} /> Reset to Aug 1 defaults
            </AdminButton>
            <AdminButton
              variant="approve"
              size="sm"
              onClick={onSave}
              disabled={!dirty}
              testid="policy-save"
            >
              <Save className="h-3.5 w-3.5" strokeWidth={1.75} /> Save changes
            </AdminButton>
          </div>
        }
      />

      {/* Panel 1 — Active phase */}
      <div
        data-testid="policy-panel-phase"
        className="rounded-lg border border-ink/10 bg-sand p-6"
      >
        <div className="flex items-center gap-2">
          <Settings className="h-4 w-4 text-ink" strokeWidth={1.75} />
          <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink">
            Active phase
          </span>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-olive">
          Every phase carries its own grant cap and documentation policy. Switching phase changes what the wizard enforces immediately.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {["beta", "pilot"].map((key) => {
            const meta = PHASE_META[key];
            const isActive = draft.activePhase === key;
            return (
              <button
                key={key}
                type="button"
                data-testid={`policy-phase-${key}`}
                onClick={() => patch((d) => ({ ...d, activePhase: key }))}
                className={`min-w-[220px] rounded-md border p-4 text-left transition ${
                  isActive
                    ? `border-${meta.tone} bg-${meta.tone}/[0.08] ring-1 ring-inset ring-${meta.tone}/40`
                    : "border-ink/10 bg-beige/60 hover:border-ink/25"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-[10px] font-semibold uppercase tracking-[0.2em] text-${meta.tone}`}>
                    {meta.label}
                  </span>
                  {isActive && (
                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-${meta.tone}`}>
                      <ShieldCheck className="h-3 w-3" strokeWidth={2} /> Active
                    </span>
                  )}
                </div>
                <p className="mt-2 text-sm text-ink">{meta.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Panels 2 + 3 — Phase configs */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <PhasePanel
          phase="beta"
          draft={draft}
          patch={patch}
          highlight={draft.activePhase === "beta"}
        />
        <PhasePanel
          phase="pilot"
          draft={draft}
          patch={patch}
          highlight={draft.activePhase === "pilot"}
        />
      </div>

      {/* Panel 4 — Campaign lifecycle */}
      <div
        data-testid="policy-panel-lifecycle"
        className="rounded-lg border border-ink/10 bg-sand p-6"
      >
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink">
            Campaign lifecycle
          </span>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-olive">
          Applies in both phases. Change once, applies to every new campaign from the next submission forward.
        </p>

        <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
          <NumberField
            label="Initial campaign duration (days)"
            testid="policy-duration"
            value={draft.campaignDurationDays}
            onChange={(v) => patch((d) => ({ ...d, campaignDurationDays: v }))}
            hint="Default 30 days."
          />
          <NumberField
            label="Extension length (days)"
            testid="policy-extension-days"
            value={draft.extensionDays}
            onChange={(v) => patch((d) => ({ ...d, extensionDays: v }))}
            hint="Default 15 days per extension."
          />
          <NumberField
            label="Extensions allowed per campaign"
            testid="policy-extensions-allowed"
            value={draft.extensionsAllowed}
            onChange={(v) => patch((d) => ({ ...d, extensionsAllowed: v }))}
            hint="Default 1. Every extension still requires admin approval."
          />
          <div>
            <p className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">
              Disbursement cadence
            </p>
            <p className="mt-2 rounded-md border border-ink/10 bg-beige/60 px-3 py-2 text-sm text-ink">
              Weekly, in equal installments
            </p>
            <p className="mt-1 text-[11px] italic text-ink/50">
              Board policy — currently not editable from this screen.
            </p>
          </div>
        </div>

        <label className="mt-5 flex items-start gap-3 rounded-md border border-ink/10 bg-beige/60 p-3">
          <input
            type="checkbox"
            checked={draft.extensionRequiresAdminApproval}
            onChange={(e) =>
              patch((d) => ({ ...d, extensionRequiresAdminApproval: e.target.checked }))
            }
            className="mt-1 h-4 w-4 accent-ink"
            data-testid="policy-extension-approval"
          />
          <span className="text-sm text-ink">
            Extensions require admin approval
            <span className="mt-0.5 block text-[11px] italic text-ink/60">
              Recommended on. Enforces board policy that every extension is signed off by an admin.
            </span>
          </span>
        </label>
      </div>

      {/* Audit footer */}
      <div
        data-testid="policy-audit"
        className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-ink/10 bg-beige/60 px-5 py-4 text-xs text-ink/70"
      >
        <div className="flex items-center gap-2">
          <Info className="h-3.5 w-3.5 text-ink/50" strokeWidth={1.75} />
          <span>
            {policy.lastUpdatedAt
              ? `Last updated ${new Date(policy.lastUpdatedAt).toLocaleString()} by ${policy.lastUpdatedBy || "Admin"}.`
              : "No edits yet. Values are the Aug 1 2026 board-approved defaults."}
          </span>
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-ink/50">
          Stored as `platform_policy` config document
        </span>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Sub-components                                                             */
/* -------------------------------------------------------------------------- */

const PhasePanel = ({ phase, draft, patch, highlight }) => {
  const meta = PHASE_META[phase];
  const p = draft[phase] || {};
  const isBeta = phase === "beta";
  return (
    <div
      data-testid={`policy-panel-${phase}`}
      className={`rounded-lg border p-6 ${
        highlight
          ? `border-${meta.tone}/40 bg-${meta.tone}/[0.04]`
          : "border-ink/10 bg-sand"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`inline-block h-2 w-2 rounded-full bg-${meta.tone}`} />
          <span className={`text-[10px] font-semibold uppercase tracking-[0.22em] text-${meta.tone}`}>
            {meta.label}
          </span>
        </div>
        {highlight && (
          <span className={`inline-flex items-center gap-1 rounded-full bg-${meta.tone}/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-${meta.tone}`}>
            <ShieldCheck className="h-3 w-3" strokeWidth={2} /> Active
          </span>
        )}
      </div>

      <NumberField
        label="Grant cap per campaign (USD)"
        testid={`policy-${phase}-cap`}
        value={p.grantCap}
        onChange={(v) =>
          patch((d) => ({ ...d, [phase]: { ...d[phase], grantCap: v } }))
        }
        hint={
          isBeta
            ? "Board-approved: $500 for beta."
            : "TBD — board will set after evaluating beta results."
        }
        allowNull={!isBeta}
        className="mt-5"
      />

      <div className="mt-5">
        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">
          Documentation requirement
        </p>
        {isBeta ? (
          <p className="mt-2 rounded-md border border-ink/10 bg-beige/60 px-3 py-2 text-sm text-ink">
            Not required in beta
          </p>
        ) : (
          <NumberField
            label={null}
            testid={`policy-${phase}-docs`}
            value={p.documentationThreshold}
            onChange={(v) =>
              patch((d) => ({
                ...d,
                [phase]: { ...d[phase], documentationThreshold: v },
              }))
            }
            hint={`Docs required for any pilot-phase campaign requesting more than the threshold. Board default: $${DEFAULT_POLICY.pilot.documentationThreshold}.`}
            allowNull
          />
        )}
        {isBeta && (
          <p className="mt-1 text-[11px] italic text-ink/50">
            Cap is $500, so nothing exceeds it. Docs become live once the pilot phase activates.
          </p>
        )}
      </div>
    </div>
  );
};

const NumberField = ({ label, value, onChange, hint, testid, allowNull = false, className = "" }) => (
  <div className={className}>
    {label != null && (
      <label className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">
        {label}
      </label>
    )}
    <input
      type="number"
      data-testid={testid}
      value={value == null ? "" : value}
      placeholder={allowNull ? "TBD" : "—"}
      onChange={(e) => {
        const v = e.target.value;
        if (v === "" && allowNull) onChange(null);
        else onChange(Number(v));
      }}
      className="mt-2 w-full rounded-md border border-ink/15 bg-bone px-3 py-2 text-sm text-ink placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-ink/20"
    />
    {hint && <p className="mt-1 text-[11px] italic text-ink/60">{hint}</p>}
  </div>
);
